import { Property, ReactSimpleComponentProps, SimpleSupplierProps } from '@/custom-types';
import { useMemo, useReducer, useRef, useState } from 'react';
import { CreateIncidendContext, CreateIncidentActionsProps } from './CreateIncidentContext';
import { CreateIncidentReducer } from '@/reducers/CreateIncidentReducer';
import {
  CreateIncidentInput,
  IncidentCategoryOption,
  IncidentTypeOption,
  PriorityOption,
  UserTypeOption,
} from '@/graphql/generated';

const emptyData: CreateIncidentInput = {
  propertyId: '',
  unityId: null,
  incidentCategory: IncidentCategoryOption.Other,
  incidentNote: '',
  incidentPriority: PriorityOption.Low, 
  incidentType: IncidentTypeOption.Fault,
  startDate: new Date().toISOString(),
  endDate: new Date().toISOString(),
  supplierId: null,
  userType: UserTypeOption.Manager,
  subject: '',
  uploadedFilesIds: [],
}

export const CreateIncidentContextProvider = ({ children }: ReactSimpleComponentProps) => {
  const [state, dispatch] = useReducer(CreateIncidentReducer, emptyData)
  const propertyRef = useRef<Property | null>(null)
  const supplierRef = useRef<SimpleSupplierProps | null>(null)
  
  const [pictures, setPictures] = useState<File[]>([])
  const [videos, setVideos] = useState<File[]>([])

  const [isDisabled, setIsDisabled] = useState(false);

  const contextActions = useMemo<Omit<CreateIncidentActionsProps, 'state' | 'buttonState' | 'supplier' | 'property' | 'pictures' | 'videos'>>(() => ({
    setProperty: (property: Property) => {
      propertyRef.current = property
      dispatch({
        type: 'CREATE_INCIDENT-setProperty',
        payload: { propertyId: property.id }
      })
    },

    setUnity: (unityId: string | null) => {
      dispatch({
        type: 'CREATE_INCIDENT-setUnity',
        payload: { unityId }
      })
    },

    setType: (type: IncidentTypeOption) => {
      dispatch({
        type: 'CREATE_INCIDENT-setType',
        payload: { type }
      })
    },

    setCategory: (category: IncidentCategoryOption) => {
      dispatch({
        type: 'CREATE_INCIDENT-setCategory',
        payload: { category }
      })
    },

    setPriority: (priority: PriorityOption) => {
      dispatch({
        type: 'CREATE_INCIDENT-setPriority',
        payload: { priority }
      })
    },

    setStartDate: (date: string | null) => {
      dispatch({
        type: 'CREATE_INCIDENT-setStartDate',
        payload: {
          date,
        }
      })
    },

    setEndDate: (date: string | null) => {
      dispatch({
        type: 'CREATE_INCIDENT-setEndDate',
        payload: {
          date,
        }
      })
    },

    setNote: (description: string) => {
      dispatch({
        type: 'CREATE_INCIDENT-setDescriptionNote',
        payload: { description }
      })
    },

    setSupplier: (supplier: SimpleSupplierProps | null) => {
      supplierRef.current = supplier
      dispatch({
        type: 'CREATE_INCIDENT-setSupplier',
        payload: { supplierId: supplier?.user.id ?? null }
      })
    },

    setSubject: (subject: string | null) => {
      dispatch({
        type: 'CREATE_INCIDENT-setSubject',
        payload: { subject: subject ?? ''}
      })
    },

    setPictures: (pictures: File[]) => {
        setPictures(pictures)
    },

    setVideos: (videos: File[]) => {
        setVideos(videos)
    },

    setButtonDisabled: () => setIsDisabled(true),

    setButtonEnabled: () => setIsDisabled(false)
  }), [])
  
  return (
    <CreateIncidendContext.Provider value={{
      ...contextActions,
      state,
      pictures,
      videos,
      buttonState: isDisabled,
      property: propertyRef.current,
      supplier: supplierRef.current,
    }}>
      {children}
    </CreateIncidendContext.Provider>
  )
}
